import { Select } from "antd";
import { Suspense, useCallback, useState, useTransition } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { useConnectionNodes } from "../Pagination";

import type { OrganizationsFilter_query$key } from "__generated__/OrganizationsFilter_query.graphql";
import type { OrganizationsFilterQuery } from "__generated__/OrganizationsFilterQuery.graphql";
import type { OrganizationsFilterRefetchQuery } from "__generated__/OrganizationsFilterRefetchQuery.graphql";

const organizationsFragment = graphql`
  fragment OrganizationsFilter_query on Query
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "OrganizationsFilterRefetchQuery") {
    organizations(after: $cursor, first: $count, searchTerm: $searchTerm)
      @connection(key: "OrganizationsFilter_organizations") {
      totalCount
      edges {
        node {
          id
          uuid
          name
        }
      }
    }
  }
`;

const query = graphql`
  query OrganizationsFilterQuery {
    ...OrganizationsFilter_query
  }
`;

interface Props {
  placeholder: string;
  value: string[];
  onChange: (value: string[]) => void;
  maxSelectionCount?: number;
}

const OrganizationsFilter = ({ placeholder, value, onChange, maxSelectionCount = 0 }: Props) => {
  const data = useLazyLoadQuery<OrganizationsFilterQuery>(query, {});

  const [isPending, startTransition] = useTransition();
  const paginationQuery = usePaginationFragment<
    OrganizationsFilterRefetchQuery,
    OrganizationsFilter_query$key
  >(organizationsFragment, data);

  const [selectedValues, setSelectedValues] = useState<Array<string>>([]);

  const handleSearch = useCallback(
    (searchTerm: string) => {
      startTransition(() => {
        paginationQuery.refetch({ searchTerm });
      });
    },
    [paginationQuery, startTransition]
  );

  const organizations = useConnectionNodes(paginationQuery.data?.organizations);

  const options = organizations.map(o => ({ label: o.name, value: o.uuid }));

  const hideDropdown = maxSelectionCount > 0 && selectedValues.length >= maxSelectionCount;
  const dropdownProps = {
    ...(hideDropdown && { className: "hidden" }),
  };

  return (
    <Select<string[]>
      mode="multiple"
      placeholder={placeholder}
      className="w-72"
      onChange={(values: string[]) => {
        setSelectedValues(values);
        onChange(values);
      }}
      onSearch={handleSearch}
      dropdownRender={menu => <div {...dropdownProps}>{menu}</div>}
      loading={isPending}
      options={options}
      filterOption={false}
    />
  );
};

const SuspenseWrappedOrganizationsFilter = (props: Props) => {
  return (
    <Suspense
      fallback={
        <Select<string[]>
          mode="multiple"
          placeholder={props.placeholder}
          className="w-72"
          loading={true}
        />
      }
    >
      <OrganizationsFilter {...props} />
    </Suspense>
  );
};

export default SuspenseWrappedOrganizationsFilter;
